import React from 'react'
import { Controller, useForm } from 'react-hook-form'
import ClearIcon from '@mui/icons-material/Clear'
import { localizedStrings } from '@core/strings'
import { useModal } from '@common/GlobalModal'
import {
  Button,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  TextField,
} from '@common/material'
import { ModalDialogContainer } from '@common/modal'
import { Text } from '@fundamentals'
import { useToast } from '@core/toast'
import { CheckInSession } from '@core/entities/checkIn'
import { useForceCheckOutPermitUsers } from '@core/react-query/features/permits/permit'

type ComponentType = {
  permitId: number
  session: CheckInSession
}

export const ForceCheckOutModal: React.FC<ComponentType> = ({
  permitId,
  session,
}) => {
  const { hideModal } = useModal()
  const toast = useToast()
  const forceCheckOut = useForceCheckOutPermitUsers()
  const { control, handleSubmit, formState } = useForm<{ reason: string }>({
    mode: 'onChange',
    defaultValues: { reason: '' },
  })

  const submit = handleSubmit(({ reason }) => {
    forceCheckOut.mutate(
      { permitId, userIds: [session.user.id], reason },
      {
        onSuccess: () => {
          hideModal()
          toast.success(localizedStrings.checkedOut)
        },
        onError: (e) => toast.error(e.message),
      },
    )
  })

  return (
    <ModalDialogContainer>
      <DialogTitle
        variant='h5'
        display='flex'
        justifyContent='space-between'
        alignItems='center'
        sx={{ pr: 1, fontSize: 20 }}
      >
        {localizedStrings.forceCheckOut}
        <IconButton aria-label='close-modal' onClick={hideModal}>
          <ClearIcon />
        </IconButton>
      </DialogTitle>

      <DialogContent>
        <Text mb={2}>
          {localizedStrings.forceCheckOutDescription(
            session.user.getFullName(),
          )}
        </Text>
        <Controller
          control={control}
          name='reason'
          rules={{ required: true, maxLength: 2000 }}
          render={({ field, fieldState }) => (
            <TextField
              {...field}
              label={localizedStrings.forceCheckOutReason}
              multiline
              rows={3}
              fullWidth
              error={!!fieldState.error}
              helperText={`${field.value?.length ?? 0}/2000`}
              inputProps={{ 'data-test': 'force-check-out-reason' }}
            />
          )}
        />
      </DialogContent>

      <DialogActions sx={{ pb: 2, pr: 3 }}>
        <Button variant='outlined' onClick={hideModal}>
          {localizedStrings.cancel}
        </Button>
        <Button
          variant='contained'
          color='error'
          onClick={submit}
          disabled={!formState.isValid || forceCheckOut.isLoading}
          data-test='force-check-out-submit'
        >
          {localizedStrings.forceCheckOut}
        </Button>
      </DialogActions>
    </ModalDialogContainer>
  )
}
